import { useEffect } from 'react';
import { ComposedModal, ModalHeader, ModalBody, Button } from '@carbon/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import style from './filter-modal.module.scss';
import { FilterCategoryList } from './filter-category-list';
import { FilterForm } from './filter-form';
import { FilterPreview } from './filter-preview';
import { useFilterCommit } from './hooks';
import { useDatasetConfig, useDatasetAtoms } from '../dataset-context';

/**
 * The dataset's filter modal: categories, the active category's form and the
 * staged filters, applied together on Apply Filters.
 * @return {JSX.Element} The modal.
 */
export function FilterModal() {
  const config = useDatasetConfig();
  const atoms = useDatasetAtoms();
  const [isOpen, setIsOpen] = useAtom(atoms.filterModalOpenAtom);
  const [activeCategory, setActiveCategory] = useAtom(
    atoms.filterModalCategoryAtom
  );
  const setModalFacets = useSetAtom(atoms.modalFacetsAtom);
  const committedFacets = useAtomValue(atoms.committedFacetsAtom);
  const refreshFacets = useSetAtom(atoms.refreshFacetsAtom);
  const { pendingFilters, seed, commit, reset } = useFilterCommit();

  // Seed pending filters and refresh facets when modal opens
  useEffect(() => {
    if (isOpen) {
      seed();
      setModalFacets(committedFacets ?? null);
      refreshFacets({});
    } else {
      setModalFacets(null);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Applies the staged filters and closes. */
  const handleApply = () => {
    commit(pendingFilters);
    setIsOpen(false);
  };

  /**
   * Discards unapplied edits by snapping staged filters back to the applied
   * search, so a later reopen reflects what's actually applied.
   */
  const handleCancel = () => {
    seed();
    setIsOpen(false);
  };

  /** Resets the staged filters to the dataset defaults. */
  const handleReset = () => {
    reset();
  };

  return (
    <ComposedModal
      open={isOpen}
      onClose={handleCancel}
      size="lg"
      className={style.modal}
    >
      <ModalHeader title={`${config.label} Filters`} />
      <ModalBody>
        <div className={style.content}>
          <div className={style.categories}>
            <FilterCategoryList
              activeCategory={activeCategory}
              onSelect={setActiveCategory}
            />
          </div>
          <div className={style.form}>
            <FilterForm activeCategory={activeCategory} />
          </div>
          <div className={style.preview}>
            <FilterPreview />
          </div>
        </div>
      </ModalBody>
      <div className={style.customFooter}>
        <Button
          size="lg"
          kind="secondary"
          className={style.footerButton}
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          size="lg"
          kind="ghost"
          className={style.footerButton}
          onClick={handleReset}
        >
          Reset
        </Button>
        <Button
          size="lg"
          kind="primary"
          className={style.footerButton}
          onClick={handleApply}
        >
          Apply Filters
        </Button>
      </div>
    </ComposedModal>
  );
}
